import { useEffect, useState } from "react";
import { Clock3, MapPin, Navigation, RadioTower } from "lucide-react";
import { interventionCategoryOrder } from "./config";
import { EditableText } from "./TextEditor";

const FINAL_VISCAR_MARKER = { x: 65.4, y: 30.7 };
export const FleetSection = ({ fleet }) => (
  <section id="flotta" data-testid="fleet-section" className="section fleet-section">
    <div className="container">
      <div data-testid="fleet-header" className="section-header section-header--wide">
        <EditableText textId="fleet.header.eyebrow" defaultText="Flotta operativa Viscar" as="span" className="section-eyebrow" />
        <EditableText textId="fleet.header.title" defaultText="Flotta operativa Viscar" as="h2" />
        <EditableText textId="fleet.header.description" defaultText="Una panoramica illustrativa dei mezzi operativi Viscar, mantenuta compatta per lasciare più spazio alle sezioni di intervento." as="p" multiline />
      </div>
      <div data-testid="fleet-grid" className="fleet-grid">
        {fleet.map((vehicle, index) => (
          <article data-testid={`fleet-card-${index}`} key={`${vehicle.category}-${index}`} className="fleet-card fleet-card--compact">
            <div className="fleet-card__media">
              {vehicle.image ? (
                <img data-testid={`fleet-card-${index}-image`} src={vehicle.image} alt="Mezzo operativo Viscar" />
              ) : (
                <div data-testid={`fleet-card-${index}-image-placeholder`} className="empty-photo-placeholder empty-photo-placeholder--fleet">&nbsp;</div>
              )}
            </div>
            <div data-testid={`fleet-card-${index}-content`} className="fleet-card__content fleet-card__content--compact">
              <EditableText textId={`fleet.card.${index}.category`} defaultText={vehicle.category} as="span" testId={`fleet-card-${index}-category`} className="card-tag" mode="click" />
              <EditableText textId={`fleet.card.${index}.description`} defaultText={vehicle.description} as="p" testId={`fleet-card-${index}-description`} multiline />
              <div data-testid={`fleet-card-${index}-badges`} className="badge-row badge-row--fleet">
                {vehicle.badges.map((badge, badgeIndex) => <EditableText key={badge} textId={`fleet.card.${index}.badge.${badgeIndex}`} defaultText={badge} as="span" mode="click" />)}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const RotatingGalleryCategory = ({ category }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (category.photos.length <= 1) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % category.photos.length);
    }, 20000);

    return () => window.clearInterval(intervalId);
  }, [category.photos.length]);

  const normalizedIndex = category.photos.length ? activeIndex % category.photos.length : 0;
  const activePhoto = category.photos[normalizedIndex];
  const hasPhotos = category.photos.length > 0;

  return (
    <article data-testid={`intervention-category-${category.key}`} className="intervention-category-card">
      <div data-testid={`intervention-category-${category.key}-header`} className="intervention-category-header">
        <EditableText textId={`gallery.category.${category.key}.label`} defaultText={category.label} as="span" testId={`intervention-category-${category.key}-label`} mode="click" />
        <EditableText textId={`gallery.category.${category.key}.description`} defaultText={category.description} as="strong" testId={`intervention-category-${category.key}-description`} mode="click" />
      </div>

      <div data-testid={`intervention-category-${category.key}-frame`} className="intervention-category-frame">
        {hasPhotos ? (
          <img
            data-testid={`intervention-category-${category.key}-active-image`}
            key={activePhoto.image}
            src={activePhoto.image}
            alt={activePhoto.title}
            loading="lazy"
            className="intervention-category-image"
          />
        ) : (
          <div data-testid={`intervention-category-${category.key}-empty`} className="empty-photo-placeholder empty-photo-placeholder--gallery">&nbsp;</div>
        )}
      </div>

      {hasPhotos ? (
        <div data-testid={`intervention-category-${category.key}-dots`} className="intervention-category-dots">
          {category.photos.map((photo, photoIndex) => (
            <span
              data-testid={`intervention-category-${category.key}-dot-${photoIndex}`}
              key={photo.image}
              className={photoIndex === normalizedIndex ? "is-active" : ""}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
};

export const InterventionGallerySection = ({ interventionPhotos }) => {
  const groupedPhotos = interventionCategoryOrder
    .map((category) => ({
      ...category,
      photos: interventionPhotos.filter((photo) => photo.category === category.key),
    }));

  return (
    <section id="interventi-reali" data-testid="intervention-gallery-section" className="section intervention-gallery-section">
      <div className="container">
        <div data-testid="intervention-gallery-groups" className="intervention-gallery-groups">
          {groupedPhotos.map((category) => (
            <RotatingGalleryCategory
              key={category.key}
              category={category}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export const OperationalAssetsSection = ({ interventionPhotos, fleet }) => {
  const groupedPhotos = interventionCategoryOrder
    .map((category) => ({
      ...category,
      photos: interventionPhotos.filter((photo) => photo.category === category.key),
    }));

  return (
    <section id="interventi-reali" data-testid="operational-assets-section" className="section intervention-gallery-section">
      <div className="container operational-assets-stack">
        <div data-testid="operational-assets-header" className="section-header section-header--wide operational-assets-header">
          <EditableText textId="operational.assets.eyebrow" defaultText="Mezzi e operatività H24" as="span" className="section-eyebrow" testId="operational-assets-eyebrow" />
          <EditableText textId="operational.assets.title" defaultText="Flotta operativa e interventi reali coordinati dalla centrale Viscar" as="h2" testId="operational-assets-title" />
          <EditableText textId="operational.assets.description" defaultText="Recovery pesante, soccorso leggero, officina mobile e supporto su A18, A20 e Messina valorizzati con i mezzi e le operazioni che rappresentano il lavoro reale di Viscar." as="p" testId="operational-assets-description" multiline />
        </div>
        <div data-testid="operational-assets-banner" className="operational-assets-banner">
          <div data-testid="operational-assets-banner-item-0" className="operational-assets-banner__item"><Clock3 size={18} /><EditableText textId="operational.assets.banner.0" defaultText="Centrale H24" as="span" mode="click" /></div>
          <div data-testid="operational-assets-banner-item-1" className="operational-assets-banner__item"><Navigation size={18} /><EditableText textId="operational.assets.banner.1" defaultText="Messina • Tremestieri • A18 • A20" as="span" mode="click" /></div>
          <div data-testid="operational-assets-banner-item-2" className="operational-assets-banner__item"><RadioTower size={18} /><EditableText textId="operational.assets.banner.2" defaultText="Soccorso leggero, heavy recovery e officina mobile" as="span" mode="click" /></div>
        </div>
        <div data-testid="intervention-gallery-groups" className="intervention-gallery-groups">
          {groupedPhotos.map((category) => (
            <RotatingGalleryCategory
              key={category.key}
              category={category}
            />
          ))}
        </div>

        <div data-testid="operational-assets-fleet" className="fleet-grid operational-assets-fleet-grid">
          {fleet.map((vehicle, index) => (
            <article data-testid={`fleet-card-${index}`} key={`${vehicle.category}-${index}`} className={`fleet-card fleet-card--compact ${index === 0 ? "fleet-card--priority" : ""}`}>
              <div className="fleet-card__media">
                {vehicle.image ? (
                  <img data-testid={`fleet-card-${index}-image`} src={vehicle.image} alt="Mezzo operativo Viscar" />
                ) : (
                  <div data-testid={`fleet-card-${index}-image-placeholder`} className="empty-photo-placeholder empty-photo-placeholder--fleet">&nbsp;</div>
                )}
              </div>
              <div data-testid={`fleet-card-${index}-content`} className="fleet-card__content fleet-card__content--compact">
                <EditableText textId={`fleet.card.${index}.category`} defaultText={vehicle.category} as="span" testId={`fleet-card-${index}-category`} className="card-tag" mode="click" />
                <EditableText textId={`fleet.card.${index}.description`} defaultText={vehicle.description} as="p" testId={`fleet-card-${index}-description`} multiline />
                <div data-testid={`fleet-card-${index}-badges`} className="badge-row badge-row--fleet">
                  {vehicle.badges.map((badge, badgeIndex) => <EditableText key={badge} textId={`fleet.card.${index}.badge.${badgeIndex}`} defaultText={badge} as="span" mode="click" />)}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CoverageSection = () => {
  const points = [
    { key: "viscar", label: "Base Viscar", className: "map-point--viscar" },
  ];

  const mapStyle = {
    "--viscar-x": `${FINAL_VISCAR_MARKER.x}%`,
    "--viscar-y": `${FINAL_VISCAR_MARKER.y}%`,
  };

  return (
    <section id="copertura" data-testid="coverage-section" className="section coverage-section">
      <div className="container coverage-grid">
        <div data-testid="coverage-content" className="split-content">
          <EditableText textId="coverage.header.eyebrow" defaultText="Area operativa" as="span" className="section-eyebrow" />
          <EditableText textId="coverage.header.title" defaultText="Base operativa a Messina, vicina a Tremestieri, con copertura A18 e A20" as="h2" />
          <EditableText textId="coverage.description" defaultText="La centrale Viscar opera da Messina, a pochi minuti dallo svincolo di Tremestieri, per coordinare soccorso stradale leggero e pesante, recovery, officina mobile e assistenza tecnica su strada e autostrada." as="p" testId="coverage-description" multiline />
          <div data-testid="coverage-stat-grid" className="coverage-stats">
            <article data-testid="coverage-stat-h24"><Clock3 /><EditableText textId="coverage.stat.h24.title" defaultText="Centrale H24" as="strong" mode="click" /><EditableText textId="coverage.stat.h24.text" defaultText="Coordinamento continuo" as="span" mode="click" /></article>
            <article data-testid="coverage-stat-a18a20"><Navigation /><EditableText textId="coverage.stat.a18.title" defaultText="A18 / A20" as="strong" mode="click" /><EditableText textId="coverage.stat.a18.text" defaultText="Copertura autostradale" as="span" mode="click" /></article>
            <article data-testid="coverage-stat-messina"><MapPin /><EditableText textId="coverage.stat.messina.title" defaultText="Tremestieri" as="strong" mode="click" /><EditableText textId="coverage.stat.messina.text" defaultText="Base vicina allo svincolo" as="span" mode="click" /></article>
            <article data-testid="coverage-stat-territory"><RadioTower /><EditableText textId="coverage.stat.dispatch.title" defaultText="Recovery pesante" as="strong" mode="click" /><EditableText textId="coverage.stat.dispatch.text" defaultText="Mezzi leggeri e industriali" as="span" mode="click" /></article>
          </div>
          <div data-testid="coverage-command-note" className="coverage-command-note"><span /><EditableText textId="coverage.command.note" defaultText="Da Messina la centrale Viscar coordina l’uscita del mezzo più adatto verso città, Tremestieri, A18 e A20." as="p" mode="click" multiline /></div>
        </div>
        <div data-testid="coverage-map" className="coverage-map" style={mapStyle}>
          <img
            data-testid="coverage-real-map-base"
            className="coverage-real-map-base"
            src="/assets/viscar/viscar_real_map_dispatch_base.jpg"
            alt="Mappa territoriale reale area Messina, Taormina e Milazzo"
          />
          <div data-testid="coverage-dispatch-overlay" className="coverage-dispatch-overlay" />
          <div data-testid="coverage-map-grid" className="map-grid-bg" />
          <div data-testid="coverage-presence-a20" className="coverage-presence coverage-presence--a20" />
          <div data-testid="coverage-presence-a18" className="coverage-presence coverage-presence--a18" />
          <div data-testid="coverage-presence-territory" className="coverage-presence-territory" />
          <div data-testid="coverage-radar-ring-primary" className="radar-ring radar-ring--primary" />
          <div data-testid="coverage-radar-ring-secondary" className="radar-ring radar-ring--secondary" />
          <div data-testid="coverage-map-scan" className="map-scan" />
          {points.map((point) => (
            <div data-testid={`coverage-point-${point.key}`} key={point.key} className={`map-point ${point.className}`}>
              <span />
              <EditableText textId={`coverage.point.${point.key}`} defaultText={point.label} as="strong" mode="click" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};